import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "reparateur" };

const ROLE_LABEL = {
  admin: { t: "Administrateur", c: "bg-red-500/10 text-red-400 border-red-500/30" },
  secretariat: { t: "Secrétariat", c: "bg-purple-500/10 text-purple-400 border-purple-500/30" },
  reparateur: { t: "Mécanicien", c: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/auth/register", form);
      toast.success("Utilisateur créé"); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try {
      await api.delete(`/users/${id}`); toast.success("Utilisateur supprimé"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC]">Utilisateurs</h1>
          <p className="text-[#94A3B8] text-sm">Gérez les accès du secrétariat et des mécaniciens.</p>
        </div>
        <Button data-testid="add-user-button" onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0D14] font-semibold">
          <UserPlus className="w-4 h-4 mr-2" /> Nouvel utilisateur
        </Button>
      </div>

      <div className="bg-[#182232] border border-[#2A364F] rounded-xl divide-y divide-[#2A364F]">
        {users.map((u) => {
          const r = ROLE_LABEL[u.role] || ROLE_LABEL.reparateur;
          return (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#3B82F6]/15 text-[#3B82F6] flex items-center justify-center font-heading font-bold">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div>
                  <div className="text-[#F8FAFC] font-medium flex items-center gap-2">
                    {u.name}
                    {u.role === "admin" && <ShieldCheck className="w-4 h-4 text-red-400" />}
                  </div>
                  <div className="text-xs text-[#64748B]">{u.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[11px] px-2.5 py-1 rounded border ${r.c}`}>{r.t}</span>
                {u.role !== "admin" && u.id !== user.id && (
                  <button data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#EF4444] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC]">
          <DialogHeader><DialogTitle className="font-heading">Nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[#94A3B8]">Nom complet *</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Email *</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Mot de passe *</Label><Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div>
              <Label className="text-[#94A3B8]">Rôle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reparateur">Mécanicien (accès restreint)</SelectItem>
                  <SelectItem value="secretariat">Secrétariat (accès complet)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-user-button" onClick={create} disabled={!form.name || !form.email || !form.password} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
